import React from 'react';
import gql from 'graphql-tag';

import client from '../../../utils/apolloClient';
import { getCachedPage, setCachedPage } from './menuItemsCache';

const updateMenuItemsMutation = gql`
  mutation updateMenuItems($input: [MenuItemInput]) {
    update_menu_items(input: $input) {
      menu_item_id
      error {
        description
      }
    }
  }
`;

export const updateMenuItems = async ({ input }) => {
  try {
    const updateMenuItemsResponse = await client.clientPrivate.mutate({
      client: client.clientPrivate,
      mutation: updateMenuItemsMutation,
      variables: {
        input,
      },
    });

    if (
      updateMenuItemsResponse?.data?.update_menu_items &&
      Array.isArray(updateMenuItemsResponse?.data?.update_menu_items)
    ) {
      const failedItemUpdate = updateMenuItemsResponse?.data?.update_menu_items.find(
        ele => ele.error && Array.isArray(ele.error),
      );

      if (
        failedItemUpdate &&
        failedItemUpdate.error[0] &&
        failedItemUpdate.error[0].description
      ) {
        return {
          error: failedItemUpdate.error[0].description,
          isError: true,
        };
      }
    }

    return {
      error: null,
      isError: false,
    };
  } catch {
    return {
      error:
        "Oops! Something didn't work as expected. Please contact support if the issue persists.",
      isError: true,
    };
  }
};

const searchMenuItemsQuery = gql`
  query searchMenuItemsQuery($input: SearchInput) {
    search_menu_items(input: $input) {
      total_size
      total_pages
      menu_item_listing {
        place_id
        platform
        place {
          name
          city
          place_id
        }
        modifier_group_id
        item_id
        item_dictionary_id
        menu_item_id
        name
        internal_name
        slug
        description
        status
        source_type
        selling_type
        tag
        sell_item_on_its_own
        price
        no_of_variants
        variant {
          prompt
          prompt_id
          variants {
            variant_name
            variant_price
            variant_id
          }
        }
        popular
        allow_item_review
        availability_status

        price
        tax
        cuisine_style {
          name
          description
          display_order
        }
        item_type {
          name
          description
          display_order
        }
        dietary_information {
          name
          description
          display_order
        }
        item_contains_alcohol
        calories
        kilojoules
        label {
          key
          value
        }
        spice_levels
        item_stored
        sell_item_on_its_own
        popular_addon
        allow_special_instructions
        audit {
          created_at
          created_by
          updated_at
          updated_by
        }
        created_by_user {
          user_id
          display_name
          email
        }
        updated_by_user {
          user_id
          display_name
          email
        }
      }
    }
  }
`;

const PAGE_SIZE = 40;

const searchMenuItems = async ({
  userId,
  placeName,
  currentPage,
  saleType,
}) => {
  const sort = 'UPDATED_DESC';
  const input = { user_id: userId };

  const menu_item_filter = {
    status: 'ACTIVE',
    ...(saleType !== 'ALL'
      ? {
          selling_type: saleType,
        }
      : {}),
  };

  const place_filter = {};
  Object.assign(place_filter, { place_name: placeName });

  Object.assign(
    input,
    { sort },
    { from: currentPage * PAGE_SIZE, size: PAGE_SIZE },
    { filter: { menu_item_filter, place_filter } },
  );

  try {
    const data = await client.clientPrivate.query({
      client: client.clientPrivate,
      query: searchMenuItemsQuery,
      variables: {
        input,
      },
      fetchPolicy: 'network-only',
    });

    if (
      data &&
      data.data &&
      data.data.search_menu_items &&
      Array.isArray(data.data.search_menu_items.menu_item_listing)
    ) {
      return data.data.search_menu_items;
    }

    return null;
  } catch {
    return null;
  }
};

export const usePlaceMenuItems = ({ userId, placeName, saleType }) => {
  const [loading, setLoading] = React.useState(true);
  const [loadingProgress, setLoadingProgress] = React.useState(null);
  const [placeMenuItems, setPlaceMenuItems] = React.useState(null);
  const [placeMenuItemsData, setPlaceMenuItemsData] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    const cacheParams = { userId, placeName, saleType };

    const fetchFunc = async () => {
      setLoading(true);
      setPlaceMenuItems(null);
      setPlaceMenuItemsData(null);

      try {
        // 1. Fetch page 0 first so we know how many pages there are.
        // totalPages is null until this first response comes back.
        setLoadingProgress({ page: 1, totalPages: null });

        const firstPageResponse = await searchMenuItems({
          userId,
          placeName,
          currentPage: 0,
          saleType,
        });

        if (!firstPageResponse) {
          throw new Error('No response from the api!');
        }

        await setCachedPage(
          cacheParams,
          0,
          firstPageResponse.menu_item_listing,
        );

        const totalPages = firstPageResponse.total_pages || 1;
        setLoadingProgress({ page: 1, totalPages });

        // 2. Fetch and cache every remaining page, one at a time. The
        // spinner stays up for this entire loop — no partial reveal.
        for (let pageIndex = 1; pageIndex < totalPages; pageIndex += 1) {
          if (cancelled) return;
          setLoadingProgress({ page: pageIndex + 1, totalPages });
          // eslint-disable-next-line no-await-in-loop
          const pageResponse = await searchMenuItems({
            userId,
            placeName,
            currentPage: pageIndex,
            saleType,
          });
          // eslint-disable-next-line no-await-in-loop
          await setCachedPage(
            cacheParams,
            pageIndex,
            pageResponse ? pageResponse.menu_item_listing : [],
          );
        }

        if (cancelled) return;

        // 3. Read every page back out of the cache — the cache, not the
        // in-memory fetch responses, is what actually gets rendered.
        const pages = [];
        for (let pageIndex = 0; pageIndex < totalPages; pageIndex += 1) {
          // eslint-disable-next-line no-await-in-loop
          const cachedPage = await getCachedPage(cacheParams, pageIndex);
          pages.push(cachedPage || []);
        }

        if (cancelled) return;

        const items = pages.flat();
        const dict = {};
        items.forEach(menuItem => {
          dict[menuItem.menu_item_id] = menuItem;
        });

        setPlaceMenuItems(dict);
        setPlaceMenuItemsData(items);
      } catch {
        // pass
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoadingProgress(null);
        }
      }
    };

    fetchFunc();

    return () => {
      cancelled = true;
    };
  }, [userId, placeName, saleType]);

  return [
    loading,
    placeMenuItems,
    placeMenuItemsData,
    setPlaceMenuItemsData,
    loadingProgress,
  ];
};

export default {};
